import type { MetricKey } from '@muzly/voice-engine';

/**
 * metricGrades — 지표별 **신뢰도 등급**과 그 근거 문구(순수 설정).
 *
 * 왜 앱이 갖나(엔진이 아니라): 같은 산식이라도 **앱이 무엇을 먹였는지**에 따라 근거의
 * 세기가 달라진다. landing은 WebAudio FFT로 스펙트럼을 넘겨 공명 축이 실측이지만,
 * 온보딩의 네이티브 경로는 raw PCM을 못 넘겨(GOAL.md) 같은 축이 미측정이다. 엔진이 등급
 * 하나를 박으면 둘 중 한쪽에서 반드시 거짓이 된다. `Metric` 타입을 안 건드리는 쪽이라
 * landing·admin 파급도 없다(플랜 Boundaries의 "Ask First" 항목).
 *
 * 등급은 리소스 p17 분류를 **우리 실제 산식에 맞춰 다시 매긴 것**이다(티켓 02).
 * 요점: "견고"를 붙일 수 있는 건 호흡·음역 둘뿐이다.
 *
 * 문구는 전부 i18n 키다 — 여기는 키만 들고 화면이 번역한다. 라벨을 모듈 초기화 때
 * 문자열로 굳히면 소비처가 `Object.fromEntries`로 한 번 떠먹고 끝나서, 언어를 바꿔도
 * 축 이름만 한국어로 남는다(홈 추세 토글에서 실제로 그랬다).
 *
 * React 의존 없음 — vitest 대상.
 */

export type MetricGrade = 'solid' | 'proxy' | 'weak';

export const GRADE_LABEL_KEY: Record<MetricGrade, string> = {
  solid: 'web.metrics.grade.solid',
  proxy: 'web.metrics.grade.proxy',
  weak: 'web.metrics.grade.weak',
};

/** 점수 카드로 내는 축 — 저장 대상(STORED_METRIC_KEYS)과 같은 4개. */
export type ScoredMetricKey = Extract<
  MetricKey,
  'sustain' | 'pitchAccuracy' | 'pitchStability' | 'vibrato'
>;

export interface MetricPresentation {
  labelKey: string;
  grade: MetricGrade;
  /** 등급이 왜 그런지 — **산식의 실제 성질**을 말한다. 홍보 문구가 아니다. */
  noteKey: string;
}

export interface ScoredMetric {
  key: ScoredMetricKey;
  card: MetricPresentation;
}

const cardKeys = (
  key: ScoredMetricKey,
): Pick<MetricPresentation, 'labelKey' | 'noteKey'> => ({
  labelKey: `web.metrics.${key}.label`,
  noteKey: `web.metrics.${key}.note`,
});

/**
 * 화면에 내는 순서 — 근거가 센 것부터.
 *
 * **음정 정확도 축은 노래를 불러도 승격되지 않는다.** 이 축의 값은 언제나 지속음에서 나온
 * 프록시(가장 가까운 반음 대비)이고, 레퍼런스 대비 채점(`songScoring`)은 **다른 눈금의 다른
 * 값**이다(프록시는 못 틀리는 반면 채점은 정답 음에서 반음 벗어나면 0점). 등급은 "이 회차에
 * 뭐가 더 있었나"가 아니라 **값의 출처**를 말하는 것이라, 값이 프록시인 채로 배지만 견고로
 * 올리면 화면이 거짓말을 한다.
 *
 * 그렇다고 축 값을 채점 결과로 바꿔치는 것도 안 된다: 같은 축의 시계열에 눈금이 다른 두 값이
 * 섞이면 노래를 건너뛴 회차가 추세에서 성장·폭락으로 보인다(홈 추세선이 목소리가 아니라
 * "무엇을 쟀나"를 그리게 된다). `Clip.hnrs`에 네이티브 dB를 못 싣는 것과 **같은 종류의
 * 함정**이다(framesToClips 모듈 주석).
 *
 * 레퍼런스 대비 채점은 결과 화면의 **별개 항목**("노래 음정 정확도", 진단 3)으로 나가고,
 * 그 항목을 그릴지 말지가 `songScore != null`의 유일한 소비처다.
 */
export const scoredMetrics = (): ScoredMetric[] => [
  {
    key: 'sustain',
    card: {
      ...cardKeys('sustain'),
      grade: 'solid',
      // 근거 문구(web.metrics.sustain.note)가 눈금 차이를 먼저 밝힌다: 점수 눈금(GOAL =
      // 12초 만점)과 참고 밴드(15~20초, resultCopy.MPT_BAND)는 **다른 자**다. 결과 화면이
      // 둘을 같은 섹션에 내므로, 18초에 100점이 뜨면서 "평균보다 짧아요"가 같이 보이면
      // 둘 중 하나가 거짓말처럼 읽힌다.
    },
  },
  {
    key: 'pitchAccuracy',
    card: {
      ...cardKeys('pitchAccuracy'),
      grade: 'proxy',
      // 목표 멜로디가 없으니 "가장 가까운 반음 대비"다 — 노래를 부른 회차에서도 이 축의
      // 값은 여전히 지속음에서 나온다(위 모듈 주석).
    },
  },
  {
    key: 'pitchStability',
    card: {
      ...cardKeys('pitchStability'),
      grade: 'proxy',
      // 리소스가 "견고"라 한 안정성은 jitter·shimmer·HNR 기반이다. 우리 건 다른 지표라
      // 같은 이름으로 그 등급을 참칭하면 안 된다.
    },
  },
  {
    key: 'vibrato',
    card: {
      ...cardKeys('vibrato'),
      grade: 'weak',
      // 리소스 8개 물리지표에 대응 항목이 아예 없다 — 우리가 만든 휴리스틱이다.
    },
  },
];

/*
 * 최대발성지속시간 참고 구간은 여기 없다 — 덱의 단일 밴드(15~20초)가 정본이고
 * `resultCopy.MPT_BAND`가 소유한다. 성별 분기(Kent 1987 표)는 삭제했다: 표시와 판정이
 * 다른 자를 쓰면 화면이 "평균 안"이라면서 다른 줄은 "짧다"고 말한다.
 */
